import { createContext, useContext, useEffect, useState, useRef } from "react";
import { Buildings_Get_All, Buildings_Get } from '../api/API_Buildings';
import { Floors_Get_All, Floors_Get } from '../api/API_Floors';
import { Rooms_Get_All, Rooms_Get } from "../api/API_Rooms";
import { useStatus } from "./Context_Status_Global";
import { Context_UI } from "./Context_UI";

const Context_MapRenderer = createContext();

export default function Context_Provider_MapRenderer({ children }) {
    const UI = useContext(Context_UI);
    const [Buildings, Buildings_Set] = useState([]);
    const [Floors, Floors_Set] = useState([]);
    const [Rooms, Rooms_Set] = useState([]);
    const [Version, Version_Set] = useState(0);
    const [Map_Composition, Map_Composition_Set] = useState([]);
    const [Map_Composition_Deleted, Map_Composition_Deleted_Set] = useState([]);
    const [Selected_Building, Selected_Building_Set] = useState(0);
    const [Selected_Floor, Selected_Floor_Set] = useState(0);
    const [Selected_Room, Selected_Room_Set] = useState(0);
    const { Status_Saving_Set, Status_Fetching_Set, Status_Refresh_Set } = useStatus();
    const [Map_HighlightedMarker, Map_HighlightedMarker_Set] = useState(null);
    const [Map_Information_Type, Map_Information_Type_Set] = useState(null);
    const [Map_Information, Map_Information_Set] = useState(null);
    const [Map_Selector_Window_Mode, Map_Selector_Window_Mode_Set] = useState({
        Activities_Visible: false
    });

    const Transformer_Reference = useRef(null);
    const Container_Reference = useRef(null);

    useEffect(() => {
        Status_Fetching_Set("InProgress");
        Buildings_Get_All()
            .then(Results => {
                Buildings_Set(Results.data);
                Status_Fetching_Set("Success");
            });
        Status_Fetching_Set("InProgress");
        Floors_Get_All()
            .then(Results => {
                Floors_Set(Results.data);
                Status_Fetching_Set("Success");
            });
        Status_Fetching_Set("InProgress");
        Rooms_Get_All()
            .then(Results => {
                Rooms_Set(Results.data);
                Status_Fetching_Set("Success");
            });
        Composition_Build();
    }, [Version]);

    useEffect(() => {
        if (Buildings.length || Floors.length || Rooms.length) {
            Composition_Build();
            Composition_Deleted_Build();
        } else {
            Map_Composition_Set([]);
            Map_Composition_Deleted_Set([]);
        }
    }, [Buildings, Floors, Rooms]);

    const Map_Refresh = () => {
        Version_Set(prev => prev + 1);
    }

    useEffect(() => {
        if (Map_Composition.length > 0 && Map_Information === null) {

            let Room_First = null;
            for (const building of Map_Composition) {
                if (building.Floors && building.Floors.length > 0) {
                    for (const floor of building.Floors) {
                        if (floor.Rooms && floor.Rooms.length > 0) {
                            Room_First = floor.Rooms[0];
                            break; 
                        }
                    }
                }
                if (Room_First) break;
            }

            if (Room_First) {
                Map_Information_Set(Room_First);
                Map_Information_Type_Set("Room");
            }
        }
    }, [Map_Composition, Map_Information]);

    const Composition_Build = () => {
        Status_Fetching_Set("InProgress");
        const Working_Composition = Buildings
            .filter(Building => !Building.isDeleted)
            .map(Building => ({
                ...Building,
                Floors: []
            }));

        for (let Floor of Floors.filter(Floor => !Floor.isDeleted)) {
            const Index = Working_Composition.findIndex(b => b._id === Floor.Building);
            if (Index !== -1) {
                Working_Composition[Index].Floors.push({ ...Floor, Rooms: [] });
            }
        }

        for (let Room of Rooms.filter(Room => !Room.isDeleted)) {
            for (let Building of Working_Composition) {
                const Index_Floor = Building.Floors.findIndex(Floor => Floor._id === Room.Location.Floor);
                if (Index_Floor !== -1) {
                    const Index_Building = Working_Composition.findIndex(Building => Building._id === Room.Location.Building);
                    const Object_Floor = Working_Composition[Index_Building]?.Floors.find(Floor => Floor._id === Room.Location.Floor);
                    if (Object_Floor) {
                        Object_Floor.Rooms.push(Room);
                    }
                }
            }
        }

        for (const building of Working_Composition) {
            building.Floors.sort((a, b) => a.Floor - b.Floor);
            for (const floor of building.Floors) {
                floor.Rooms.sort((a, b) => a.Location.Code - b.Location.Code);
            }
        }

        Map_Composition_Set(Working_Composition);
        console.log("Map composition built.");
        Status_Fetching_Set("Success");
    }

    const Composition_Deleted_Build = () => {
        Status_Fetching_Set("InProgress");
        const Working_Composition = Buildings
            .filter(Building => Building.isDeleted)
            .map(Building => ({
                ...Building,
                Floors: []
            }));

        for (let Floor of Floors.filter(Floor => Floor.isDeleted)) {
            const Index = Working_Composition.findIndex(b => b._id === Floor.Building);
            if (Index !== -1) {
                Working_Composition[Index].Floors.push({ ...Floor, Rooms: [] });
            }
        }

        for (let Room of Rooms.filter(Room => Room.isDeleted)) {
            for (let Building of Working_Composition) {
                const Index_Floor = Building.Floors.findIndex(Floor => Floor._id === Room.Location.Floor);
                if (Index_Floor !== -1) {
                    const Index_Building = Working_Composition.findIndex(Building => Building._id === Room.Location.Building);
                    const Object_Floor = Working_Composition[Index_Building]?.Floors.find(Floor => Floor._id === Room.Location.Floor);
                    if (Object_Floor) {
                        Object_Floor.Rooms.push(Room);
                    }
                }
            }
        }

        Map_Composition_Deleted_Set(Working_Composition);
        // console.log("Built composition:", Working_Composition);
        Status_Fetching_Set("Success");
    }

    const Get_Parent_Index = (Room_ID) => {
        // find the room object first
        const room = Rooms.find(r => String(r._id) === String(Room_ID));
        if (!room) return null; // not found

        const ID_Building = String(room.Location.Building);
        const ID_Floor = String(room.Location.Floor);
        const ID_Room = String(room._id);

        // find building index in Map_Composition
        const Index_Building = Map_Composition.findIndex(b => String(b._id) === ID_Building);
        if (Index_Building === -1) return null;

        // find floor index inside that building
        const Index_Floor = (Map_Composition[Index_Building].Floors || [])
            .findIndex(f => String(f._id) === ID_Floor);
        if (Index_Floor === -1) return null;

        // find room index inside that floor
        const Index_Room = (Map_Composition[Index_Building].Floors[Index_Floor].Rooms || [])
            .findIndex(r => String(r._id) === ID_Room);

        return {
            Index_Building,
            Index_Floor,
            Index_Room,
            ID_Building,
            ID_Floor,
            ID_Room
        };
    };

    const Room_Find = (ID) => {
        for (let a = 0; a < Map_Composition.length; a++) {
            const Building = Map_Composition[a];
            for (let b = 0; b < Building.Floors.length; b++) {
                const Floor = Building.Floors[b];
                const Room = Floor.Rooms.find(Room => Room._id == ID);
                if (Room) {
                    return {
                        Room,
                        Building: a,
                        Floor: b,
                        Building_Object: Map_Composition[a],
                        Floor_Object: Map_Composition[a].Floors[b]
                    }
                }
            }
        }
        return null;
    }

    const Room_Highlight = async (ID, ScaleTarget, Duration, Source) => {
        const Data = Room_Find(ID);
        if (!Data) { return; }
        const Room = Data.Room;
        const Building = Data.Building;
        const Floor = Data.Floor;

        let Timeout = 10;
        const Wrapper = Transformer_Reference.current;
        if (Building != Selected_Building || Floor != Selected_Floor) {
            Selected_Building_Set(Building);
            Selected_Floor_Set(Floor);
            if (Source == "Directory") {
                Wrapper.setTransform(0, 0, 1, 400);
            }

            Timeout = 500;
        }

        // 

        // await new Promise(r => requestAnimationFrame(r));
        setTimeout(function () {
            if (!Wrapper) { return; }
            const Marker = document.getElementById(ID);
            if (!Marker) { return; }
            if (Source == "Directory") {
                Wrapper.zoomToElement(Marker, ScaleTarget, Duration);
            } else {
                Wrapper.zoomToElement(Marker);
            }

            Map_HighlightedMarker_Set(ID);
        }, Timeout);

    }

    const Room_Center = async (ID, Duration, Source) => {
        const Data = Room_Find(ID);
        if (!Data) { return; }
        const Room = Data.Room;
        const Building = Data.Building;
        const Floor = Data.Floor;

        let Timeout = 10;
        const Wrapper = Transformer_Reference.current;
        if (Building != Selected_Building || Floor != Selected_Floor) {
            Selected_Building_Set(Building);
            Selected_Floor_Set(Floor);
            if (Source == "Directory") {
                Wrapper.setTransform(0, 0, 1, 400);
            }

            Timeout = 500;
        }

        // 

        // await new Promise(r => requestAnimationFrame(r));
        setTimeout(function () {
            if (!Wrapper) { return; }
            const Marker = document.getElementById(ID);
            if (!Marker) { return; }
            if (Source == "Directory") {
                Wrapper.centerElement(Marker, Duration);
            } else {
                Wrapper.centerElement(Marker);
            }

            Map_HighlightedMarker_Set(ID);
        }, Timeout);
    }

    const Building_Switch = (Building_Index) => {
        Selected_Building_Set(Building_Index);
        Selected_Floor_Set(0);
        const Wrapper = Transformer_Reference.current;
        Wrapper.setTransform(0, 0, 1, 400);
        Map_HighlightedMarker_Set(null);
    }

    const Floor_Switch = (Building_Index, Floor_Index) => {
        Selected_Building_Set(Building_Index);
        Selected_Floor_Set(Floor_Index);
        const Wrapper = Transformer_Reference.current;
        Wrapper.setTransform(0, 0, 1, 400);
        Map_HighlightedMarker_Set(null);
    }

    const Information_Display = async (Type, ID) => {
        if (!Type) { return; }
        Status_Fetching_Set("InProgress");
        try {
            let Response = {};
            if (Type == "Building") {
                Response = await Buildings_Get(ID);
            }
            if (Type == "Floor") {
                Response = await Floors_Get(ID);
            }
            if (Type == "Room") {
                Response = await Rooms_Get(ID);
            }
            if (!Response) { return; }
            Map_Information_Set(Response.data);
            Map_Information_Type_Set(Type);
            Status_Fetching_Set("Success");
        } catch (Error) {
            Status_Fetching_Set("Error");
            console.error(Error);
        }

    }

    const Room_Get_Address = (RoomID, Part) => {
        const found = Room_Find(RoomID);
        if (!found) return "Unknown Room";
        // console.log(found);
        if (!Part) {
            return `${found.Building_Object.Name}, ${found.Floor_Object.Name}, ${found.Room.Name}`;
        } else if (Part === "Building") {
            return `${found.Building_Object.Name}`;
        } else if (Part === "Floor") {
            return `${found.Floor_Object.Name}`;
        } else if (Part === "Room") {
            return `${found.Room.Name}`;
        }

    };

    const Now = new Date();
    const Hours = String(Now.getHours()).padStart(2, '0');
    const Minutes = String(Now.getMinutes()).padStart(2, '0');
    const [Map_Time_Parameters, Map_Time_Parameters_Set] = useState({
        Date: new Date().toISOString().split("T")[0],
        Time_Start: `${Hours}:${Minutes}`,
        Time_End: "23:59"
    });

    useEffect(() => {
        if (UI.Element_Get_ByID("Controls_Map_Selector_Date")) {
            Map_Time_Parameters_Update();
        }
    }, [])

    const Map_Time_Parameters_Update = (Type, Value) => {
        if (Type == "Date") {
            Map_Time_Parameters_Set(Prev => (
                {
                    ...Prev,
                    Date: Value
                }
            ));
        }
        if (Type == "Time_Start") {
            Map_Time_Parameters_Set(Prev => (
                {
                    ...Prev,
                    Time_Start: Value
                }
            ));
        }
        if (Type == "Time_End") {
            Map_Time_Parameters_Set(Prev => (
                {
                    ...Prev,
                    Time_End: Value
                }
            ));
        }

        // console.log("Updated");
    }


    return (
        <Context_MapRenderer.Provider value={{
            Buildings,
            Buildings_Set,
            Floors,
            Floors_Set,
            Rooms,
            Rooms_Set,
            Version,
            Map_Refresh,
            Map_Composition,
            Map_Composition_Set,
            Composition_Build,
            Map_Composition_Deleted,
            Map_Composition_Deleted_Set,
            Composition_Deleted_Build,
            Map_HighlightedMarker,
            Map_HighlightedMarker_Set,
            Selected_Building,
            Selected_Building_Set,
            Selected_Floor,
            Selected_Floor_Set,
            Selected_Room,
            Selected_Room_Set,
            Room_Find, Room_Highlight,
            Transformer_Reference,
            Container_Reference,
            Building_Switch,
            Floor_Switch,
            Map_Information,
            Map_Information_Set,
            Map_Information_Type,
            Map_Information_Type_Set,
            Information_Display,
            Room_Center,
            Get_Parent_Index,
            Room_Get_Address,
            Map_Time_Parameters,
            Map_Time_Parameters_Update,
            Map_Selector_Window_Mode,
            Map_Selector_Window_Mode_Set
        }}>
            {children}
        </Context_MapRenderer.Provider>
    )
}

export function useMapRenderer() {
    return useContext(Context_MapRenderer);
}